import type { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { Plus, Clock } from "lucide-react";
import { useMutation } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableSkeleton } from "@/components/data-table/data-table-skeleton";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useReservations, useCargosForReservation } from "@/hooks/useReservations";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { BADGE_TONES, pickAssortedBadgeClass } from "@/lib/badge-tones";
import { formatStatusLabel } from "@/lib/format";
import { getReservationStatusBadgeClass } from "@/lib/status-tag-colors";
import { useAppLocale } from "@/lib/i18n";
import { sortByLabel } from "@/lib/sort";
import { m } from "@/paraglide/messages.js";

type ReservationCargoOption = {
  _id: string;
  ociNumber: string;
  productName: string;
  actuallyAvailable: number;
};

function formatReservationType(type?: string) {
  if (type === "checkout") return "Payment Lock";
  return type ? formatStatusLabel(type) : m.not_available();
}

function formatDuration(duration?: string) {
  if (!duration) return m.not_available();
  if (duration === "1hr") return m.duration_1_hour();
  if (duration === "1day") return m.duration_1_day();
  if (duration === "3day") return m.duration_3_days();
  return duration;
}

function getReservationCargoOptionLabel(cargo: ReservationCargoOption) {
  return m.reservation_cargo_option({
    oci: cargo.ociNumber,
    product: cargo.productName,
    available: cargo.actuallyAvailable,
  });
}

function getReservationCargoSortLabel(cargo: ReservationCargoOption) {
  return `${cargo.ociNumber} ${cargo.productName}`;
}

function useColumns(onRelease: (reservation: any) => void) {
  useAppLocale();
  return React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "actions",
        header: "",
        size: 80,
        enableSorting: false,
        cell: ({ row }) => {
          if (row.original.status === "active") {
            return <Button variant="outline" size="sm" onClick={() => onRelease(row.original)}>{m.release()}</Button>;
          }
          return null;
        },
      },
      {
        id: "cargoOci",
        accessorKey: "cargoOci",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.cargo()} />,
        meta: { label: m.cargo_oci(), placeholder: m.search_cargo_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "cargoProduct",
        accessorKey: "cargoProduct",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.product()} />,
        meta: { label: m.product(), placeholder: m.search_product_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "quantity",
        accessorKey: "quantity",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.quantity_short()} />,
        meta: { label: m.quantity(), variant: "number", placeholder: m.minimum_quantity_placeholder() },
        enableColumnFilter: true,
      },
      {
        id: "type",
        accessorKey: "type",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.type()} />,
        cell: ({ row }) => {
          const type = row.original.type;
          return type ? (
            <Badge variant="outline" className={pickAssortedBadgeClass(type)}>
              {formatReservationType(type)}
            </Badge>
          ) : (
            <span className="text-muted-foreground">{m.not_available()}</span>
          );
        },
        meta: { label: m.type(), placeholder: m.search_type_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "holdDuration",
        accessorFn: (row) => formatDuration(row.holdDuration),
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.duration()} />,
        cell: ({ row }) => {
          const duration = formatDuration(row.original.holdDuration);
          if (!duration || duration === m.not_available()) {
            return <span className="text-muted-foreground">{m.not_available()}</span>;
          }
          return (
            <Badge variant="outline" className={BADGE_TONES.cyan}>
              {duration}
            </Badge>
          );
        },
        meta: { label: m.duration(), placeholder: m.search_duration_placeholder(), variant: "text" },
      },
      {
        id: "status",
        accessorKey: "status",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.status()} />,
        cell: ({ row }) => (
          <Badge variant="outline" className={getReservationStatusBadgeClass(row.original.status)}>
            {formatStatusLabel(row.original.status)}
          </Badge>
        ),
        meta: {
          label: m.status(),
          variant: "select",
          options: [
            { label: m.status_active(), value: "active" },
            { label: m.status_expired(), value: "expired" },
            { label: m.status_released(), value: "released" },
            { label: m.status_converted(), value: "converted" },
          ],
        },
        enableColumnFilter: true,
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.created()} />,
        cell: ({ row }) => {
          const ts = row.original.createdAt;
          return ts ? new Date(ts).toLocaleString() : m.not_available();
        },
        meta: { label: m.created(), placeholder: m.search_date_placeholder(), variant: "date" },
      },
      {
        id: "expiresAt",
        accessorKey: "expiresAt",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.expires()} />,
        cell: ({ row }) => {
          if (row.original.creditKeyAttemptId && row.original.status === "active") return "Until Credit Key is resolved";
          const ts = row.original.expiresAt;
          return ts ? new Date(ts).toLocaleString() : m.not_available();
        },
        meta: { label: m.expires(), placeholder: m.search_date_placeholder(), variant: "date" },
      },
      {
        id: "notes",
        accessorKey: "notes",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.notes()} />,
        meta: { label: m.notes(), placeholder: m.search_notes_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
    ],
    [onRelease],
  );
}

const statusFilters = ["all", "active", "expired", "released", "converted"] as const;

export default function InventoryReservationsPage() {
  const { locale } = useAppLocale();
  const { isPending } = useAuthIdentity();
  const reservations = useReservations();
  const cargos = useCargosForReservation();
  const createHoldMutation = useMutation(api.admin.createManualInventoryHold);
  const releaseHoldMutation = useMutation(api.admin.releaseInventoryHold);

  const [statusFilter, setStatusFilter] = React.useState<string>("all");
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [releaseDialogOpen, setReleaseDialogOpen] = React.useState(false);
  const [selectedReservation, setSelectedReservation] = React.useState<any>(null);

  // Create form
  const [createForm, setCreateForm] = React.useState({ cargoId: "", quantity: 1, holdDuration: "1day" as string, notes: "" });
  const [isCreating, setIsCreating] = React.useState(false);

  // Release form
  const [releaseReason, setReleaseReason] = React.useState("");
  const [isReleasing, setIsReleasing] = React.useState(false);

  const filteredReservations = React.useMemo(() => {
    const data = reservations ?? [];
    if (statusFilter === "all") return data;
    return data.filter((r: any) => r.status === statusFilter);
  }, [reservations, statusFilter]);
  const sortedCargos = React.useMemo(
    () => sortByLabel(cargos as ReservationCargoOption[] | undefined, getReservationCargoSortLabel, locale),
    [cargos, locale],
  );

  const openReleaseDialog = React.useCallback((reservation: any) => {
    setSelectedReservation(reservation);
    setReleaseReason("");
    setReleaseDialogOpen(true);
  }, []);

  const columns = useColumns(openReleaseDialog);

  const handleCreate = React.useCallback(async () => {
    if (!createForm.cargoId) { toast.error(m.select_cargo_first()); return; }
    setIsCreating(true);
    try {
      await createHoldMutation({
        cargoId: createForm.cargoId as any,
        quantity: createForm.quantity,
        holdDuration: createForm.holdDuration as any,
        notes: createForm.notes || undefined,
      });
      toast.success(m.inventory_hold_created());
      setCreateDialogOpen(false);
      setCreateForm({ cargoId: "", quantity: 1, holdDuration: "1day", notes: "" });
    } catch (e: any) {
      toast.error(e.message || m.inventory_hold_create_failed());
    } finally {
      setIsCreating(false);
    }
  }, [createForm, createHoldMutation]);

  const handleRelease = React.useCallback(async () => {
    if (!selectedReservation || !releaseReason.trim()) { toast.error(m.provide_reason()); return; }
    setIsReleasing(true);
    try {
      await releaseHoldMutation({
        reservationId: selectedReservation._id,
        reason: releaseReason.trim(),
      });
      toast.success(m.inventory_hold_released());
      setReleaseDialogOpen(false);
      setSelectedReservation(null);
      setReleaseReason("");
    } catch (e: any) {
      toast.error(e.message || m.inventory_hold_release_failed());
    } finally {
      setIsReleasing(false);
    }
  }, [selectedReservation, releaseReason, releaseHoldMutation]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{m.inventory_reservations_title()}</h1>
          <p className="text-muted-foreground mt-1">
            {m.inventory_reservations_description()}
            {filteredReservations.length > 0 ? (
              <span className="ml-1 font-medium text-foreground">{m.inventory_reservations_count({ count: filteredReservations.length })}</span>
            ) : null}
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          {m.new_hold()}
        </Button>
      </div>

      <div className="flex gap-2">
        {statusFilters.map((sf) => (
            <Button key={sf} variant={statusFilter === sf ? "default" : "outline"} size="sm" onClick={() => setStatusFilter(sf)}>
            {sf === "active" ? <><Clock className="mr-1 h-3 w-3" />{formatStatusLabel(sf, m.all())}</> : formatStatusLabel(sf, m.all())}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{m.reservations()}</CardTitle>
          <CardDescription>{m.inventory_reservations_card_description()}</CardDescription>
        </CardHeader>
        <CardContent>
          {reservations === undefined || (isPending && filteredReservations.length === 0) ? (
            <DataTableSkeleton columnCount={columns.length} rowCount={10} withPagination />
          ) : (
          <InternalEntityDataTable
            data={filteredReservations.map((r: any) => ({ ...r, id: r._id || r.id }))}
            columns={columns}
            initialSorting={[{ id: "createdAt", desc: true }]}
            enableSelection={false}
          />
          )}
        </CardContent>
      </Card>

      {/* Create Hold Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{m.create_inventory_hold()}</DialogTitle>
            <DialogDescription>{m.create_inventory_hold_description()}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>{m.cargo()}</Label>
              <Select value={createForm.cargoId} onValueChange={(v) => setCreateForm((p) => ({ ...p, cargoId: v }))}>
                <SelectTrigger className="w-full truncate"><SelectValue placeholder={m.select_cargo_placeholder()} /></SelectTrigger>
                <SelectContent position="popper" className="max-w-[var(--radix-select-trigger-width)]">
                  {sortedCargos.map((cargo) => (
                    <SelectItem key={cargo._id} value={cargo._id} className="truncate">
                      {getReservationCargoOptionLabel(cargo)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>{m.quantity_bags()}</Label>
              <Input type="number" min={1} value={createForm.quantity} onChange={(e) => setCreateForm((p) => ({ ...p, quantity: parseInt(e.target.value) || 1 }))} />
            </div>
            <div className="grid gap-2">
              <Label>{m.hold_duration()}</Label>
              <div className="flex gap-2">
                {(["1hr", "1day", "3day"] as const).map((d) => (
                  <Button key={d} variant={createForm.holdDuration === d ? "default" : "outline"} size="sm" onClick={() => setCreateForm((p) => ({ ...p, holdDuration: d }))}>
                    {d === "1hr" ? m.duration_1_hour() : d === "1day" ? m.duration_1_day() : m.duration_3_days()}
                  </Button>
                ))}
              </div>
            </div>
            <div className="grid gap-2">
              <Label>{m.notes_optional()}</Label>
              <Textarea placeholder={m.inventory_hold_notes_placeholder()} value={createForm.notes} onChange={(e) => setCreateForm((p) => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>{m.cancel()}</Button>
            <Button onClick={() => void handleCreate()} disabled={isCreating}>{isCreating ? m.creating() : m.create_hold()}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Release Hold Dialog */}
      <Dialog open={releaseDialogOpen} onOpenChange={setReleaseDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>{m.release_inventory_hold()}</DialogTitle>
            <DialogDescription>
              {selectedReservation ? m.release_inventory_hold_description({ quantity: selectedReservation.quantity, product: selectedReservation.cargoProduct }) : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>{m.reason_for_release()}</Label>
              <Textarea placeholder={m.reason_for_release_placeholder()} value={releaseReason} onChange={(e) => setReleaseReason(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReleaseDialogOpen(false)}>{m.cancel()}</Button>
            <Button onClick={() => void handleRelease()} disabled={isReleasing || !releaseReason.trim()}>{isReleasing ? m.releasing() : m.release_hold()}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
